import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

/**
 * TokenInterceptor
 *
 * HTTP interceptor that automatically adds authentication tokens to outgoing
 * HTTP requests and handles token refresh on 401 Unauthorized responses.
 *
 * This interceptor implements the following flows:
 * 1. Adds Authorization header with Bearer token to API requests
 * 2. Skips authentication for excluded URLs (auth endpoints, public APIs)
 * 3. Handles 401 errors by attempting token refresh
 * 4. Prevents multiple simultaneous refresh attempts
 * 5. Redirects to login if refresh fails
 *
 * Token Refresh Flow:
 * 1. Request receives 401 Unauthorized
 * 2. Interceptor attempts to refresh token (only once for multiple requests)
 * 3. If refresh succeeds: Retry original request with new token
 * 4. If refresh fails: Clear auth state and redirect to login
 *
 * Usage:
 * Register in app.module.ts or app.config.ts:
 * ```typescript
 * providers: [
 *   {
 *     provide: HTTP_INTERCEPTORS,
 *     useClass: TokenInterceptor,
 *     multi: true
 *   }
 * ]
 * ```
 *
 * Configuration:
 * The interceptor automatically excludes certain URLs from authentication:
 * - Auth endpoints (login, callback, token exchange)
 * - Public APIs that don't require authentication
 * - External URLs (configurable)
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private router = inject(Router);

  /**
   * Tracks if a token refresh is currently in progress
   * @private
   */
  private isRefreshing = false;

  /**
   * Subject that emits when token refresh completes
   * Used to queue requests waiting for refresh
   * @private
   */
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  /**
   * URLs that should not have authentication headers
   * @private
   */
  private excludedUrls: string[] = [
    '/auth/login',
    '/auth/callback',
    '/auth/token',
    '/auth/refresh',
    'oauth',
    'openid-connect'
  ];

  /**
   * Intercept HTTP requests to add authentication
   *
   * @param request The outgoing HTTP request
   * @param next The next handler in the chain
   * @returns Observable of the HTTP event
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip authentication for excluded URLs
    if (this.shouldSkipAuth(request)) {
      return next.handle(request);
    }

    // Add token if available
    const token = this.authService.getAccessToken();
    if (token) {
      request = this.addToken(request, token);
    }

    // Handle the request and catch 401 errors
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Add Authorization header with Bearer token
   *
   * @param request The HTTP request
   * @param token The access token
   * @returns Cloned request with Authorization header
   * @private
   */
  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  /**
   * Check if authentication should be skipped for this request
   *
   * @param request The HTTP request
   * @returns true if auth should be skipped, false otherwise
   * @private
   */
  private shouldSkipAuth(request: HttpRequest<unknown>): boolean {
    const url = request.url.toLowerCase();

    // Check if URL matches any excluded patterns
    return this.excludedUrls.some(excludedUrl =>
      url.includes(excludedUrl.toLowerCase())
    );
  }

  /**
   * Handle 401 Unauthorized error by attempting token refresh
   *
   * This method implements a queue mechanism to prevent multiple
   * simultaneous refresh attempts when multiple requests fail with 401.
   *
   * @param request The failed HTTP request
   * @param next The next handler in the chain
   * @returns Observable of the retried HTTP event
   * @private
   */
  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // Attempt to refresh token
      return new Observable<HttpEvent<unknown>>(observer => {
        this.authService.refreshToken().then(
          (success) => {
            this.isRefreshing = false;

            if (success) {
              // Token refresh succeeded
              const newToken = this.authService.getAccessToken();
              this.refreshTokenSubject.next(newToken);

              // Retry original request with new token
              if (newToken) {
                const retryRequest = this.addToken(request, newToken);
                next.handle(retryRequest).subscribe(
                  event => observer.next(event),
                  error => observer.error(error),
                  () => observer.complete()
                );
              } else {
                observer.error(new Error('Token refresh succeeded but no token available'));
              }
            } else {
              // Token refresh failed - redirect to login
              console.error('Token refresh failed, redirecting to login');
              this.handleRefreshFailure();
              observer.error(new Error('Token refresh failed'));
            }
          },
          (error) => {
            // Token refresh error
            this.isRefreshing = false;
            console.error('Token refresh error:', error);
            this.handleRefreshFailure();
            observer.error(error);
          }
        );
      });
    } else {
      // Token refresh already in progress - queue this request
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((token) => {
          if (token) {
            const retryRequest = this.addToken(request, token);
            return next.handle(retryRequest);
          }
          return throwError(() => new Error('Token refresh failed'));
        })
      );
    }
  }

  /**
   * Handle refresh failure by clearing auth state and redirecting to login
   * @private
   */
  private handleRefreshFailure(): void {
    this.authService.logout();
  }
}
